'use client';

import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { DataGrid, DataGridSortModel, FilterAutoCompleteById, Button } from '@repo/components';
import { Site, ZonesApiResponse, FeatureFlagsSettings } from '@repo/shared/domain';
import { ZoneForm } from '@repo/forms';
import { EventEmitter, guardedPost, guardedPut, constructQuery } from '@repo/shared/helpers';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import Paper from '@mui/material/Paper';
import { sortToString, stringToSort } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { useConfig } from 'src/hooks/useConfig';
import { getZones, getAllSites } from 'src/api/admin/sites';

interface ZonesSearch {
	sort?: string;
	page?: number;
	siteIds?: string;
}

export const Route = createFileRoute('/_admin/config/zones')({
	validateSearch: (search: Record<string, unknown>): ZonesSearch => ({
		sort: search.sort as string | undefined,
		page: search.page as number | undefined,
		siteIds: search.siteIds as string | undefined,
	}),
	component: ZonesPage,
});

const getColumns = (usesEmergencyModule: boolean) => [
	{
		field: 'name',
		headerName: 'Name',
		flex: 1,
	},
	{
		field: 'description',
		headerName: 'Description',
		flex: 3,
		sortable: false,
	},
	{
		field: 'site.name',
		headerName: 'Site name',
		flex: 1,
		sortable: false,
	},
	{
		field: 'isVirtual',
		headerName: 'Virtual',
		width: 150,
		renderCell: (c: { value: boolean }) => (c.value ? <CheckIcon /> : ''),
	},
	...(usesEmergencyModule
		? [
				{
					field: 'isMusterZone',
					headerName: 'Muster Zone',
					width: 150,
					renderCell: (c: { value: boolean }) => (c.value ? <CheckIcon /> : ''),
				},
			]
		: []),
	{
		field: 'updatedAt',
		headerName: 'Updated',
		width: 150,
		valueFormatter: (params: { value: string }) => new Date(params.value).toLocaleDateString('en-GB'),
	},
];

function ZonesPage() {
	const searchParams = Route.useSearch();
	const { sort, page, siteIds } = searchParams;
	const queryClient = useQueryClient();
	const updateQueryParams = useUpdateQueryParams();
	const { config } = useConfig();
	const featureFlags = config?.featureFlags || {};

	const [isFormOpen, setIsFormOpen] = useState(false);
	const [activeZone, setActiveZone] = useState<ZonesApiResponse['data'][number] | undefined>();

	useBreadcrumbLevel(0, 'Configuration');
	useBreadcrumbLevel(1, 'Zones');

	const query = constructQuery({
		noPagination: 'false',
		sort: sort || 'name:asc',
		page,
		siteIds,
	});

	const { data: zonesData, isLoading: zonesLoading } = useQuery({
		queryKey: ['zones', query],
		queryFn: () => getZones(query),
	});

	const { data: sitesData } = useQuery({
		queryKey: ['sites-all'],
		queryFn: () => getAllSites(),
	});

	const zonesApiResponse = zonesData || { data: [], count: 0, page: 0, pageSize: 25 };
	const sites: Site[] = sitesData?.data || [];

	const handleCloseForm = () => {
		setIsFormOpen(false);
		setActiveZone(undefined);
	};

	const handleOpenForm = () => {
		setIsFormOpen(true);
		setActiveZone(undefined);
	};

	const handleSort = ([sortItem]: DataGridSortModel) => {
		if (!sortItem) {
			return;
		}
		updateQueryParams({ sort: sortItem ? sortToString(sortItem) : undefined });
	};

	const handleFilterChange = (filterData: { siteIds?: string[] | string }) => {
		updateQueryParams(filterData);
	};

	const handleSubmit = async (data: Record<string, unknown>) => {
		let isError;
		if (data) {
			const method = data.id ? guardedPut : guardedPost;
			await method(`/api/zones/${data.id || ''}`, data)
				.then(() => {
					EventEmitter.emit('toast', {
						type: 'success',
						message: `Zone saved`,
					});
				})
				.catch(() => {
					isError = true;
					EventEmitter.emit('toast', {
						type: 'error',
						message: `Failed to save company type. Did you enter all data correctly?`,
					});
				});
		}
		revalidateZones();
		if (!isError) {
			setIsFormOpen(false);
			setActiveZone(undefined);
		}
	};

	const handleRowClick = ({ row }: { id: string; row: ZonesApiResponse['data'][number] | undefined }) => {
		if (row) {
			setActiveZone(row);
		}
	};

	const handlePaginationModelChange = ({ page: newPage }: { page: number }) => {
		updateQueryParams({ page: newPage });
	};

	const revalidateZones = () => {
		queryClient.invalidateQueries({ queryKey: ['zones'] });
	};

	const columns = getColumns(featureFlags.USES_EMERGENCY_MODULE || false);
	return (
		<>
			<div className="p-4">
				<div key="search" className="flex justify-between gap-2">
					<FilterAutoCompleteById
						key="siteNameFilter"
						label="Filter Sites"
						options={sites}
						value={siteIds || []}
						onChange={(newSiteIds) => handleFilterChange({ siteIds: newSiteIds })}
						multiple
						sx={{ minWidth: 360, width: 'auto' }}
					/>
					<div>
						<Button key="add" onClick={handleOpenForm} variant="outline-bg-white" className="h-full">
							<AddIcon />
							Add Zone
						</Button>
					</div>
				</div>
				<Paper variant="outlined" sx={{ marginTop: 2 }}>
					<DataGrid
						isLoading={zonesLoading}
						data={zonesApiResponse.data}
						rowCount={zonesApiResponse.count}
						page={zonesApiResponse.page}
						pageSize={zonesApiResponse.pageSize}
						columns={columns}
						onSortModelChange={handleSort}
						sortModel={[stringToSort(sort || 'name:asc')]}
						onReload={revalidateZones}
						onPaginationModelChange={handlePaginationModelChange}
						paginationMode="server"
						sortingMode="server"
						sortingOrder={['asc', 'desc']}
						onRowClick={handleRowClick}
					/>
				</Paper>
			</div>
			{(isFormOpen || !!activeZone) && (
				<ZoneForm
					isOpen={isFormOpen || !!activeZone}
					onSubmit={handleSubmit}
					onClose={handleCloseForm}
					data={activeZone || {}}
					sites={sites}
					featureFlags={featureFlags as FeatureFlagsSettings['settings']}
				/>
			)}
		</>
	);
}
